import React from 'react';
import { BarChart3, Eye, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const DM_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getDMHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('crm_token')}` });

const REPORTS_INDEX = [
  { srNo: 1, index: 'PROJECT LEAD COUNT', report: 'DONE', graph: 'DONE' },
  { srNo: 2, index: 'PROJECT INDEX', report: 'DONE', graph: 'DONE' },
  { srNo: 3, index: 'INVENTORY INDEX', report: 'DONE', graph: 'DONE' },
  { srNo: 4, index: 'PAYMENT INDEX', report: 'DONE', graph: 'DONE' },
  { srNo: 5, index: 'BOOKING CUSTOMER INDEX', report: 'DONE', graph: 'DONE' },
  { srNo: 6, index: 'WHATSAPP STATUS INDEX', report: 'DONE', graph: 'DONE' },
  { srNo: 7, index: 'ALL LEAD INDEX', report: 'DONE', graph: 'DONE' },
  { srNo: 8, index: 'CALLING REPORT', report: 'DONE', graph: 'DONE' },
];

const DMIntegrationTab = () => {
  const [projectLeads, setProjectLeads] = React.useState(null);
  const [projectIndex, setProjectIndex] = React.useState(null);
  const [paymentIndex, setPaymentIndex] = React.useState(null);
  const [bookingCustomers, setBookingCustomers] = React.useState(null);
  const [whatsappStatus, setWhatsappStatus] = React.useState(null);
  const [allLeadIndex, setAllLeadIndex] = React.useState(null);
  const [callingReport, setCallingReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [callingType, setCallingType] = React.useState('monthly');
  const [lastUpdated, setLastUpdated] = React.useState(null);

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [plRes, piRes, payRes, bcRes, waRes, alRes, crRes] = await Promise.all([
          fetch(`${DM_API}/dm-telecalling/project-lead-count`, { headers: getDMHeaders() }),
          fetch(`${DM_API}/dm-telecalling/project-index`, { headers: getDMHeaders() }),
          fetch(`${DM_API}/dm-telecalling/payment-index`, { headers: getDMHeaders() }),
          fetch(`${DM_API}/dm-telecalling/booking-customers`, { headers: getDMHeaders() }),
          fetch(`${DM_API}/dm-telecalling/whatsapp-status`, { headers: getDMHeaders() }),
          fetch(`${DM_API}/dm-telecalling/all-lead-index`, { headers: getDMHeaders() }),
          fetch(`${DM_API}/dm-telecalling/calling-report?report_type=monthly`, { headers: getDMHeaders() }),
        ]);
        const [pl, pi, pay, bc, wa, al, cr] = await Promise.all([plRes.json(), piRes.json(), payRes.json(), bcRes.json(), waRes.json(), alRes.json(), crRes.json()]);
        setProjectLeads(pl);
        setProjectIndex(pi);
        setPaymentIndex(pay);
        setBookingCustomers(bc);
        setWhatsappStatus(wa);
        setAllLeadIndex(al);
        setCallingReport(cr);
        setLastUpdated(new Date());
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const fetchCallingByType = async (type) => {
    setCallingType(type);
    try {
      const res = await fetch(`${DM_API}/dm-telecalling/calling-report?report_type=${type}`, { headers: getDMHeaders() });
      const data = await res.json();
      setCallingReport(data);
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
      <span className="ml-3 text-slate-500">Loading DM Integration data...</span>
    </div>
  );

  const fmt = (v) => typeof v === 'number' ? v.toLocaleString('en-IN') : (v ?? '—');
  const fmtAmt = (v) => typeof v === 'number' ? `₹${v.toLocaleString('en-IN')}` : '—';

  return (
    <div className="space-y-6">
      {/* Banner */}
      <div className="flex items-center gap-3 bg-gradient-to-r from-sky-800 to-sky-700 rounded-xl px-5 py-3">
        <BarChart3 className="w-5 h-5 text-sky-300 flex-shrink-0" />
        <div>
          <p className="text-white font-semibold text-sm">Digital Marketing Department — Data Integration</p>
          <p className="text-sky-200 text-xs">Live data from Komal Markad's Digital Marketing Dashboard — Read only</p>
        </div>
        <span className="ml-auto flex items-center gap-1.5 text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-full px-2.5 py-0.5">
          <Eye className="w-3 h-3" /> View Only
        </span>
        {lastUpdated && (
          <span className="flex items-center gap-1 text-xs text-slate-400 ml-3">
            <Clock className="w-3 h-3" />
            Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* 1. REPORTS INDEX */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-700 text-white py-3">
          <CardTitle className="text-sm font-bold tracking-wide">REPORTS INDEX</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-700 text-white">
                <th className="px-4 py-2 text-left w-14">SR.NO</th>
                <th className="px-4 py-2 text-left">INDEX</th>
                <th className="px-4 py-2 text-center">REPORT</th>
                <th className="px-4 py-2 text-center">GRAPH</th>
              </tr>
            </thead>
            <tbody>
              {REPORTS_INDEX.map((row, i) => (
                <tr key={row.srNo} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-4 py-2 text-slate-500 font-medium">{row.srNo}</td>
                  <td className="px-4 py-2 font-semibold text-slate-700">{row.index}</td>
                  <td className="px-4 py-2 text-center"><span className="bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded">{row.report}</span></td>
                  <td className="px-4 py-2 text-center"><span className="bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded">{row.graph}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Total Report index from marketing department mentioned above</p>
        </CardContent>
      </Card>

      {/* 2. PROJECT WISE LEAD COUNT */}
      {projectLeads && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-sky-700 to-sky-600 text-white py-3">
            <CardTitle className="text-sm font-bold tracking-wide">PROJECTWISE LEAD COUNT</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-0">
              <div className="overflow-auto max-h-[360px]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-sky-700 text-white">
                      <th className="px-4 py-2 text-left font-semibold">PROJECT NAME</th>
                      <th className="px-4 py-2 text-right font-semibold">Count of NO.</th>
                    </tr>
                    <tr className="bg-slate-800 text-white font-semibold">
                      <td className="px-4 py-1.5">GRAND TOTAL</td>
                      <td className="px-4 py-1.5 text-right">{fmt(projectLeads.grandTotal)}</td>
                    </tr>
                  </thead>
                  <tbody>
                    {(projectLeads.projects || []).map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-4 py-2 font-medium text-slate-700">{row.project}</td>
                        <td className="px-4 py-2 text-right font-semibold text-sky-700">{fmt(row.leadCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-center p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie isAnimationActive={false}
                      data={(projectLeads.projects || []).map(p => ({ name: p.project, value: p.leadCount }))}
                      cx="50%" cy="50%" innerRadius={55} outerRadius={100}
                      paddingAngle={2} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {(projectLeads.projects || []).map((_, idx) => (
                        <Cell key={idx} fill={['#0ea5e9','#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6','#ec4899','#14b8a6'][idx % 8]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Project wise lead list count updating</p>
          </CardContent>
        </Card>
      )}

      {/* 3. PROJECT INDEX */}
      {projectIndex?.groups && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white py-3">
            <CardTitle className="text-sm font-bold tracking-wide">PROJECT INDEX — Inventory by Group</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {projectIndex.groups.map((grp, gi) => (
              <div key={gi} className="border-b border-slate-100 last:border-0">
                <div className="bg-indigo-50 px-4 py-1.5 font-semibold text-indigo-800 text-xs uppercase tracking-wide">{grp.group}</div>
                <div className="overflow-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-indigo-700 text-white">
                        <th className="px-3 py-2 text-left w-10">SR.NO</th>
                        <th className="px-3 py-2 text-left">PROJECT NAME</th>
                        <th className="px-3 py-2 text-right">TOTAL UNITS</th>
                        <th className="px-3 py-2 text-right">LAND OWNER</th>
                        <th className="px-3 py-2 text-right">SIDDHIVINAYAK</th>
                        <th className="px-3 py-2 text-right">SOLD</th>
                        <th className="px-3 py-2 text-right">UNSOLD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grp.projects.map((proj, pi) => (
                        <tr key={pi} className={pi % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                          <td className="px-3 py-2 text-slate-500">{pi + 1}</td>
                          <td className="px-3 py-2 font-medium text-slate-700">{proj.project}</td>
                          <td className="px-3 py-2 text-right">{fmt(proj.totalUnits)}</td>
                          <td className="px-3 py-2 text-right">{fmt(proj.landOwner)}</td>
                          <td className="px-3 py-2 text-right">{fmt(proj.siddhivinayak)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-700">{fmt(proj.sold)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-rose-600">{fmt(proj.unsold)}</td>
                        </tr>
                      ))}
                      <tr className="bg-slate-800 text-white font-semibold">
                        <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                        <td className="px-3 py-2 text-right">{fmt(grp.totals?.totalUnits)}</td>
                        <td className="px-3 py-2 text-right">{fmt(grp.totals?.landOwner)}</td>
                        <td className="px-3 py-2 text-right">{fmt(grp.totals?.siddhivinayak)}</td>
                        <td className="px-3 py-2 text-right text-green-300">{fmt(grp.totals?.sold)}</td>
                        <td className="px-3 py-2 text-right text-rose-300">{fmt(grp.totals?.unsold)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Project wise inventory count — Total leads / Owner / Siddhivinayak / Sold / Unsold</p>
          </CardContent>
        </Card>
      )}

      {/* 4. PAYMENT INDEX */}
      {paymentIndex && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-emerald-700 to-emerald-600 text-white py-3">
            <CardTitle className="text-sm font-bold tracking-wide">PAYMENT INDEX — Project Wise Booking Amt. Calculation</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[360px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-emerald-700 text-white">
                    <th className="px-4 py-2 text-left">PROJECT</th>
                    <th className="px-4 py-2 text-right">TOTAL RECEIVABLE</th>
                    <th className="px-4 py-2 text-right">TOTAL RECEIVED</th>
                    <th className="px-4 py-2 text-right">TOTAL BALANCE</th>
                  </tr>
                  {paymentIndex.grandTotal && (
                    <tr className="bg-slate-800 text-white font-semibold">
                      <td className="px-4 py-1.5">GRAND TOTAL</td>
                      <td className="px-4 py-1.5 text-right">{fmtAmt(paymentIndex.grandTotal.totalReceivable)}</td>
                      <td className="px-4 py-1.5 text-right text-green-300">{fmtAmt(paymentIndex.grandTotal.totalReceived)}</td>
                      <td className="px-4 py-1.5 text-right text-rose-300">{fmtAmt(paymentIndex.grandTotal.totalBalance)}</td>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {(paymentIndex.projects || []).map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-2 font-medium text-slate-700">{row.project}</td>
                      <td className="px-4 py-2 text-right text-slate-600">{fmtAmt(row.totalReceivable)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-green-700">{fmtAmt(row.totalReceived)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-rose-600">{fmtAmt(row.totalBalance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Total Received / Total Receivable &amp; Total Balance Amt.</p>
          </CardContent>
        </Card>
      )}

      {/* 5. BOOKING CUSTOMER INDEX */}
      {bookingCustomers?.records && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-violet-700 to-violet-600 text-white py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-bold tracking-wide">BOOKING CUSTOMER INDEX</CardTitle>
            {bookingCustomers.summary && (
              <div className="flex gap-3 text-xs text-violet-200">
                <span>Total: <strong className="text-white">{fmt(bookingCustomers.summary.total)}</strong></span>
                <span>Done: <strong className="text-green-300">{fmt(bookingCustomers.summary.done)}</strong></span>
                <span>Pending: <strong className="text-rose-300">{fmt(bookingCustomers.summary.pending)}</strong></span>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[400px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-violet-700 text-white">
                    <th className="px-3 py-2 text-left w-10">SR.NO</th>
                    <th className="px-3 py-2 text-left">AGENT</th>
                    <th className="px-3 py-2 text-left">PROJECT NAME</th>
                    <th className="px-3 py-2 text-left">CONFIG</th>
                    <th className="px-3 py-2 text-left">FLAT NO</th>
                    <th className="px-3 py-2 text-left">CUSTOMER NAME</th>
                    <th className="px-3 py-2 text-right">RECEIVABLE</th>
                    <th className="px-3 py-2 text-right">RECEIVED</th>
                    <th className="px-3 py-2 text-right">BALANCE</th>
                    <th className="px-3 py-2 text-center">STATUS</th>
                    <th className="px-3 py-2 text-left">REMARK</th>
                  </tr>
                </thead>
                <tbody>
                  {bookingCustomers.records.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-3 py-1.5 text-slate-500">{row.srNo}</td>
                      <td className="px-3 py-1.5 text-slate-600">{row.agentName}</td>
                      <td className="px-3 py-1.5 font-medium text-slate-700">{row.projectName}</td>
                      <td className="px-3 py-1.5 text-slate-600">{row.configuration}</td>
                      <td className="px-3 py-1.5 text-slate-600">{row.flatNo}</td>
                      <td className="px-3 py-1.5 font-medium text-slate-700">{row.customerName}</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{fmtAmt(row.totalReceivable)}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-green-700">{fmtAmt(row.received)}</td>
                      <td className="px-3 py-1.5 text-right font-semibold text-rose-600">{fmtAmt(row.balance)}</td>
                      <td className="px-3 py-1.5 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${row.status === 'DONE' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{row.status}</span>
                      </td>
                      <td className="px-3 py-1.5 text-slate-500 max-w-[150px] truncate">{row.remark}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Customer Booking — Stage (refer Excel sheet for detailed format)</p>
          </CardContent>
        </Card>
      )}

      {/* 6. WHATSAPP STATUS INDEX */}
      {whatsappStatus?.records && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-700 to-green-600 text-white py-3">
            <CardTitle className="text-sm font-bold tracking-wide">WHATSAPP STATUS INDEX</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[360px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-green-700 text-white">
                    <th className="px-4 py-2 text-left">MONTH</th>
                    <th className="px-4 py-2 text-right">TOTAL CP</th>
                    <th className="px-4 py-2 text-right">DAILY ACTIVE CP</th>
                    <th className="px-4 py-2 text-right">INACTIVE CP</th>
                    <th className="px-4 py-2 text-right">PAYABLE CP</th>
                    <th className="px-4 py-2 text-right">AMOUNT (₹)</th>
                    <th className="px-4 py-2 text-right">PAID (₹)</th>
                    <th className="px-4 py-2 text-right">BALANCE (₹)</th>
                    <th className="px-4 py-2 text-center">STATUS</th>
                  </tr>
                  {(() => {
                    const recs = whatsappStatus.records.filter(r => r.totalCP > 0);
                    return (
                      <tr className="bg-slate-800 text-white font-semibold">
                        <td className="px-4 py-1.5">GRAND TOTAL</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + r.totalCP, 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + r.dailyActive, 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + r.dailyInactive, 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + r.paymentPayableCP, 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmtAmt(recs.reduce((s, r) => s + r.amount, 0))}</td>
                        <td className="px-4 py-1.5 text-right text-green-300">{fmtAmt(recs.reduce((s, r) => s + r.paid, 0))}</td>
                        <td className="px-4 py-1.5 text-right text-rose-300">{fmtAmt(recs.reduce((s, r) => s + r.balance, 0))}</td>
                        <td className="px-4 py-1.5 text-center">—</td>
                      </tr>
                    );
                  })()}
                </thead>
                <tbody>
                  {whatsappStatus.records.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-2 font-medium text-slate-700">{row.month}</td>
                      <td className="px-4 py-2 text-right font-semibold text-sky-700">{fmt(row.totalCP)}</td>
                      <td className="px-4 py-2 text-right text-green-700">{fmt(row.dailyActive)}</td>
                      <td className="px-4 py-2 text-right text-rose-600">{fmt(row.dailyInactive)}</td>
                      <td className="px-4 py-2 text-right text-slate-600">{fmt(row.paymentPayableCP)}</td>
                      <td className="px-4 py-2 text-right text-slate-600">{fmtAmt(row.amount)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-green-700">{fmtAmt(row.paid)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-rose-600">{fmtAmt(row.balance)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${row.status === 'PAYMENT DONE' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{row.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Total CP / Daily Active / Inactive CP Count with monthly spending — 300X Total Active CP</p>
          </CardContent>
        </Card>
      )}

      {/* 7. ALL LEAD INDEX */}
      {allLeadIndex?.records && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-amber-700 to-amber-600 text-white py-3">
            <CardTitle className="text-sm font-bold tracking-wide">ALL LEAD INDEX — Lead &amp; Conversion Stage Index</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[360px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-amber-700 text-white">
                    <th className="px-4 py-2 text-left">PROJECT / GROUP</th>
                    <th className="px-4 py-2 text-right">TOTAL LEADS</th>
                    <th className="px-4 py-2 text-right">INPROCESS</th>
                    <th className="px-4 py-2 text-right">HOLD</th>
                    <th className="px-4 py-2 text-right">CANCEL</th>
                    <th className="px-4 py-2 text-right">OFFICE VISIT</th>
                    <th className="px-4 py-2 text-right">PROJECT VISIT</th>
                    <th className="px-4 py-2 text-right">SCOPE</th>
                    <th className="px-4 py-2 text-right">DONE</th>
                  </tr>
                </thead>
                <tbody>
                  {allLeadIndex.records.map((row, i) => (
                    <tr key={i} className={row.project === 'TOTAL' ? 'bg-slate-800 text-white font-semibold' : i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className={`px-4 py-2 font-medium ${row.project === 'TOTAL' ? 'text-white' : row.projectId ? 'pl-8 text-slate-600' : 'text-slate-800'}`}>{row.project}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${row.project === 'TOTAL' ? 'text-sky-300' : 'text-sky-700'}`}>{fmt(row.totalLeads)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.inprocess)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.hold)}</td>
                      <td className={`px-4 py-2 text-right ${row.project !== 'TOTAL' ? 'text-rose-600' : 'text-rose-300'}`}>{fmt(row.cancel)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.officeVisit)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.projectVisit)}</td>
                      <td className="px-4 py-2 text-right">{fmt(row.scope)}</td>
                      <td className={`px-4 py-2 text-right font-semibold ${row.project !== 'TOTAL' ? 'text-green-700' : 'text-green-300'}`}>{fmt(row.done)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Lead &amp; conversion stage index with the current count</p>
          </CardContent>
        </Card>
      )}

      {/* 8. CALLING REPORT */}
      {callingReport?.records && (
        <Card className="border-0 shadow-md overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-rose-700 to-rose-600 text-white py-3 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-bold tracking-wide">CALLING REPORT</CardTitle>
            <div className="flex gap-1">
              {['daily', 'weekly', 'monthly'].map(t => (
                <button
                  key={t}
                  onClick={() => fetchCallingByType(t)}
                  className={`text-xs px-2 py-0.5 rounded font-medium transition-colors capitalize ${callingType === t ? 'bg-white text-rose-700' : 'bg-rose-600 text-white hover:bg-rose-500'}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto max-h-[360px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-rose-700 text-white">
                    <th className="px-4 py-2 text-left">{callingType === 'daily' ? 'DATE' : callingType === 'weekly' ? 'WEEK' : 'MONTH'}</th>
                    <th className="px-4 py-2 text-right">TOTAL CALLS</th>
                    <th className="px-4 py-2 text-right">INPROCESS</th>
                    <th className="px-4 py-2 text-right">HOLD</th>
                    <th className="px-4 py-2 text-right">CANCEL</th>
                    <th className="px-4 py-2 text-right">SITE VISIT</th>
                    <th className="px-4 py-2 text-right">DONE</th>
                  </tr>
                  {(() => {
                    const recs = callingReport.records;
                    return (
                      <tr className="bg-slate-800 text-white font-semibold">
                        <td className="px-4 py-1.5">GRAND TOTAL</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + (r.totalCalls || 0), 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + (r.inprocess || 0), 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + (r.hold || 0), 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + (r.cancel || 0), 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + (r.siteVisit || 0), 0))}</td>
                        <td className="px-4 py-1.5 text-right">{fmt(recs.reduce((s, r) => s + (r.done || 0), 0))}</td>
                      </tr>
                    );
                  })()}
                </thead>
                <tbody>
                  {callingReport.records.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-2 font-medium text-slate-700">{row.month || row.week || row.date}</td>
                      <td className="px-4 py-2 text-right font-semibold text-sky-700">{fmt(row.totalCalls)}</td>
                      <td className="px-4 py-2 text-right text-amber-600">{fmt(row.inprocess)}</td>
                      <td className="px-4 py-2 text-right text-blue-600">{fmt(row.hold)}</td>
                      <td className="px-4 py-2 text-right text-rose-600">{fmt(row.cancel)}</td>
                      <td className="px-4 py-2 text-right text-violet-600">{fmt(row.siteVisit)}</td>
                      <td className="px-4 py-2 text-right font-semibold text-green-700">{fmt(row.done)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 italic px-4 py-2 border-t border-slate-100">Calling Report as Per Daily / Weekly &amp; Monthly Count for total data</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DMIntegrationTab;
